import { Linkedin, Mail } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import defaultLogo from "@/assets/4fox-logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Fetch active site logo from database with dimension settings
  const { data: activeLogo } = useQuery({
    queryKey: ['active-site-logo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_logos')
        .select('image_url, alt_text, footer_height')
        .eq('is_active', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Add cache-busting query parameter to force browsers to reload fresh logo
  const logoSrc = activeLogo?.image_url 
    ? `${activeLogo.image_url}?v=2` 
    : defaultLogo;
  const logoAlt = activeLogo?.alt_text || "4FOX Security - Cyber Security Consultancy";
  const footerHeight = activeLogo?.footer_height ?? 56;

  // Fetch footer logos from database
  const { data: footerLogos } = useQuery({
    queryKey: ['footer-logos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_logos')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch certificate widgets from database
  const { data: certificateWidgets } = useQuery({
    queryKey: ['certificate-widgets'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_widgets')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  return (
    <footer className="bg-primary text-white py-12">
      <div className="container mx-auto px-4">
        <div className="flex justify-center mb-8">
          <div className="text-center">
            <a href="/" className="inline-block">
              <img 
                src={logoSrc} 
                alt={logoAlt} 
                width={Math.round(footerHeight * 3)}
                height={footerHeight}
                style={{ height: `${footerHeight}px` }} 
                className="w-auto mb-4 mx-auto hover:opacity-80 transition-opacity" 
                loading="lazy"
                decoding="async"
              />
            </a>
            <p className="text-white/70 max-w-2xl">
              Practical cyber security consultancy for businesses of all sizes.
            </p>
          </div>
        </div>

        {/* Certificate Widgets - dynamically loaded from database */}
        <div className="flex justify-center items-center gap-6 mb-4 flex-wrap">
          {certificateWidgets?.map((widget) => (
            <iframe
              key={widget.id}
              src={widget.iframe_src}
              style={{
                border: 'none',
                height: `${widget.height}px`,
                width: `${widget.width}px`,
              }}
              title={widget.name}
              loading="lazy"
            />
          ))}
        </div>

        {/* Dynamic certification logos */}
        <div className="flex justify-center items-center gap-6 mb-8 flex-wrap max-w-4xl mx-auto">
          {footerLogos?.map((footerLogo) => (
            <a
              key={footerLogo.id}
              href={footerLogo.link_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-24"
            >
              <img
                src={footerLogo.image_url}
                alt={footerLogo.alt_text}
                className="w-full h-14 object-contain opacity-80 hover:opacity-100 transition-opacity"
                loading="lazy"
              />
            </a>
          ))}
        </div>

        {/* Email + LinkedIn section */}
        <div className="flex justify-center items-center gap-6 mb-8">
          <a 
            href="mailto:hello@4fox.uk"
            className="flex items-center gap-2 text-white/70 hover:text-accent transition-colors group"
          >
            <Mail className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="font-medium text-lg">hello@4fox.uk</span>
          </a>
          <span className="text-white/30">|</span>
          <a 
            href="https://www.linkedin.com/company/4foxsecurity/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-white/70 hover:text-accent transition-colors group"
            aria-label="Follow us on LinkedIn"
          >
            <Linkedin className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <span className="font-medium">Follow us on LinkedIn</span>
          </a>
        </div>

        <div className="border-t border-white/10 pt-8 text-center text-white/70 text-sm">
          <p className="mb-2">
            Based in Somerset | Serving Bristol, North Somerset, South Gloucestershire & across the UK
          </p>
          <p>&copy; {currentYear} 4FOX Security. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <a href="/privacy-policy" className="hover:text-accent transition-colors">Privacy Policy</a>
            <span>·</span>
            <a href="/terms" className="hover:text-accent transition-colors">Terms of Use</a>
            <span>·</span>
            <a href="/staff-portal" className="text-white/40 hover:text-white/60 transition-colors text-xs">Staff</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
